import {
  ALL_LAYOUT_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  CONTAINER_ARGS,
  CONTENT_ARGS,
  FLEX_ARGS,
  GRID_ARGS,
  Metadata,
  POSITIONING_ARGS,
  pick,
} from '../../../utils/spds/metadata';
import {
  BOTH,
  HEIGHT,
  WIDTH,
  composeFlexShorthand,
  isFlexExpanding as flexExpands,
  hasFlexLonghandOverride as flexHasLonghandOverride,
  resolveEffectiveFlex,
} from '../../../utils/spds/css/flex';
import { Debug } from '../../../utils/spds/debug';
import {
  FIT_CONTENT,
  FIT_CONTENT_HEIGHT,
  FIT_CONTENT_WIDTH,
  FLEX,
  INITIAL,
  INLINE_FLEX,
  NONE,
} from '../../../utils/spds/constants/css-layout';
import { bool, or } from 'macro-decorators';
import { element } from 'ember-element-helper';
import { htmlSafe } from '@ember/template';
import { registerDestructor } from '@ember/destroyable';
import { runInDebug } from '@ember/debug';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { verifyHtmlTag } from '../../../utils/spds/html-verification';
import Super from './base.gjs';
import styles from './container.module.scss';

const METADATA = Metadata.define({
  description: `
Generic container component

### Customization

By default, the rendered HTML element is a \`div\` but any valid HTML element can be rendered by providing the \`@tag\` argument.

### Styling

The default CSS \`display\` for the container is \`flex\` with a \`flex-direction\` of \`row\`. It can be changed to \`inline-flex\` by
providing the \`@inline\` argument. The \`flex-direction\` can be changed to \`column\` by providing the \`@column\` argument.
Additional styling can be added to the container by assigning custom CSS classes to the component through the HTML \`class\` attribute
as normal.

The automatic flexbox layout can be turned off entirely by passing \`@flexbox={{false}}\`. In that mode the container renders in
normal document flow (e.g. \`block\` for a \`div\`), and the \`@inline\` argument switches the container to \`inline-block\` instead of
\`inline-flex\`.

Similarly, there are some arguments which can be used to quickly apply height and width sizing to restrict the size of the container:

* \`@fitContent\`: this restricts both the height and width of the container to fit its content
* \`@fitContentHeight\`: adds the size restriction to just the height of the container
* \`@fitContentWidth\`: adds the size restriction to just the width of the container

### Positioning

The \`@absolute\` and \`@fixed\` arguments will apply CSS classes that set the container's positioning to those respective values.
Specific X and Y positioning can be applied by adding a custom CSS class that provides that data.

### Discarding clicks

In the case that the content of the container should not be clickable for some reason, there is a preconfigured modifier which can be applied
to the container automatically by passing the \`@discardClick\` argument.
  `,
  extends: [
    CONTAINER_ARGS,
    POSITIONING_ARGS,
    ALL_LAYOUT_ARGS,
    pick(COMPONENT_STATE_ARGS, ['error']),
    pick(CONTAINER_ARGS, ['inline', 'tag']),
    pick(CONTENT_ARGS, ['content']),
  ],
  meta: {
    discardClick: Arg.boolean({ description: 'Prevent click events from propagating' }),
    flexbox: Arg.boolean({
      default: true,
      description:
        'Enables the default flexbox layout (`display: flex`). Set to `false` to disable automatic ' +
        'flexbox so the container renders in normal document flow; in that mode `@inline` renders ' +
        'the container as `inline-block`. Cannot be combined with `@grid`; when `@grid` is enabled ' +
        'flexbox is disabled automatically.',
    }),
    // Preserve the inherited flex arg config (cssProperty/default/forbids/type) and add the
    // override relationship: flex is discarded when a fit-content constraint applies to the
    // axis that flex governs (the parent's main axis).
    flex: {
      ...FLEX_ARGS.meta.flex,
      overriddenBy: [
        {
          name: FIT_CONTENT,
          axis: BOTH,
          reason: 'fit-content constrains both axes, so flex on the main axis is ignored',
        },
        {
          name: FIT_CONTENT_WIDTH,
          axis: WIDTH,
          reason: 'width: fit-content is ignored when flex governs the (horizontal) main axis',
        },
        {
          name: FIT_CONTENT_HEIGHT,
          axis: HEIGHT,
          reason: 'height: fit-content is ignored when flex governs the (vertical) main axis',
        },
      ],
    },
    inputContainer: Arg.boolean({ description: 'Apply input container styling' }),
  },
  name: 'Container',
});

// Cached prop names to avoid Object.keys() on every instance access
const FLEX_PROP_NAMES = Object.keys(FLEX_ARGS.meta);
const GRID_PROP_NAMES = Object.keys(GRID_ARGS.meta);

export default class SpdsCoreContainer extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);

  baseClass = 'spds-container';
  classesToMap = [
    'absolute',
    'column',
    'error',
    'fitContentHeight',
    'fitContentWidth',
    'fixed',
    'flexbox',
    'grid',
    'inline',
    'inputContainer',
    'reverse',
    'subgrid',
  ];
  styles = styles;

  @service fastboot;

  // The parent's resolved main axis ('width' | 'height' | 'none'), measured once at setup.
  // Undefined until measured (SSR / pre-insertion), where consumers fall back to @column.
  @tracked resolvedMainAxis;

  @bool('args.fitContent') fitContent;
  @bool('args.fitContentHeight') fitContentH;
  @bool('args.fitContentWidth') fitContentW;
  @bool('args.discardClick') shouldDiscardClick;

  @or('fitContent', 'fitContentH') fitContentHeight;
  @or('fitContent', 'fitContentW') fitContentWidth;

  get alignContent() {
    return this.argOrDefault('alignContent');
  }

  get alignItems() {
    return this.argOrDefault('alignItems');
  }

  get alignSelf() {
    return this.argOrDefault('alignSelf');
  }

  get column() {
    return this.args.column && !this.grid;
  }

  get content() {
    return htmlSafe(this.args.content);
  }

  get defaultGap() {
    return this.constructor.metadata.getDefault('gap');
  }

  /**
   * The effective flex `{ grow, shrink, basis }` resolved from the @flex shorthand plus
   * any individual longhand overrides (@flexGrow/@flexShrink/@flexBasis). Resolved from the
   * input args (not computed style, which would feed back from the --flex we set).
   */
  get effectiveFlex() {
    return resolveEffectiveFlex({
      flex: this.args.flex,
      flexGrow: this.args.flexGrow,
      flexShrink: this.args.flexShrink,
      flexBasis: this.args.flexBasis,
    });
  }

  get flex() {
    if (this.isMainAxisConstrained) return INITIAL;

    // Preserve the raw shorthand for the common case; only compose when a longhand override
    // is present (so the longhand args take effect through the single --flex channel).
    return this.hasFlexLonghandOverride
      ? composeFlexShorthand(this.effectiveFlex)
      : this.argOrDefault('flex');
  }

  get flexbox() {
    return !this.grid && this.argOrDefault('flexbox');
  }

  get flexWrap() {
    return this.argOrDefault('flexWrap');
  }

  get gap() {
    return this.argOrDefault('gap');
  }

  get grid() {
    return this.argOrDefault('grid');
  }

  get gridArea() {
    return this.args.gridArea ?? this.args.area ?? this.constructor.metadata.getDefault('gridArea');
  }

  get gridAutoColumns() {
    return this.argOrDefault('gridAutoColumns');
  }

  get gridAutoFlow() {
    return (
      this.args.gridAutoFlow ??
      this.args.autoFlow ??
      this.constructor.metadata.getDefault('gridAutoFlow')
    );
  }

  get gridAutoRows() {
    return this.argOrDefault('gridAutoRows');
  }

  get gridColumn() {
    return this.argOrDefault('gridColumn');
  }

  get gridRow() {
    return this.args.gridRow ?? this.args.row ?? this.constructor.metadata.getDefault('gridRow');
  }

  get gridTemplateAreas() {
    return (
      this.args.gridTemplateAreas ??
      this.args.areas ??
      this.constructor.metadata.getDefault('gridTemplateAreas')
    );
  }

  get gridTemplateColumns() {
    return (
      this.args.gridTemplateColumns ??
      this.args.columns ??
      this.constructor.metadata.getDefault('gridTemplateColumns')
    );
  }

  get gridTemplateRows() {
    return (
      this.args.gridTemplateRows ??
      this.args.rows ??
      this.constructor.metadata.getDefault('gridTemplateRows')
    );
  }

  get hasFlexLonghandOverride() {
    return flexHasLonghandOverride({
      flexGrow: this.args.flexGrow,
      flexShrink: this.args.flexShrink,
      flexBasis: this.args.flexBasis,
    });
  }

  /**
   * Whether the resolved flex makes the element non-content-sized along its main axis
   * (grow > 0 or a non-auto basis). Only such flex actually defeats main-axis fit-content.
   */
  get isFlexExpanding() {
    return flexExpands(this.effectiveFlex);
  }

  get isMainAxisConstrained() {
    if (!this.isFlexExpanding) return false;
    if (this.mainAxis === WIDTH) return this.fitContentWidth;
    if (this.mainAxis === HEIGHT) return this.fitContentHeight;

    return false; // 'none' -> parent not flex -> flex is inert, keep it
  }

  get justifyContent() {
    return this.argOrDefault('justifyContent');
  }

  get justifyItems() {
    return this.argOrDefault('justifyItems');
  }

  get justifySelf() {
    return this.argOrDefault('justifySelf');
  }

  /**
   * The axis flex governs for this container. Prefers the measured parent axis (cached at
   * setup); before measurement falls back to this container's own @column as a proxy so
   * first paint matches prior behavior.
   */
  get mainAxis() {
    if (this.resolvedMainAxis) return this.resolvedMainAxis;

    return this.column ? HEIGHT : WIDTH;
  }

  get order() {
    return this.argOrDefault('order');
  }

  /**
   * The parent's main axis ('width' | 'height'), or 'none' when the parent is not a flex
   * container (flex is inert), or undefined when not yet measurable (SSR / pre-insertion).
   * @flex resolves against the parent's flex-direction, not this container's own.
   */
  get parentMainAxis() {
    if (this.fastboot?.isFastBoot) return undefined;

    const parent = this.baseElement?.parentElement;

    if (!parent) return undefined;

    const { display, flexDirection } = window.getComputedStyle(parent);

    if (![FLEX, INLINE_FLEX].includes(display)) return NONE;

    return flexDirection.startsWith('column') ? HEIGHT : WIDTH;
  }

  get propsToMap() {
    return this.grid ? GRID_PROP_NAMES : FLEX_PROP_NAMES;
  }

  get reverse() {
    return this.args.reverse && !this.grid;
  }

  get tag() {
    let ELEMENT_TAG_DEFAULT = this.constructor.metadata.getDefault('tag');
    let { tag = ELEMENT_TAG_DEFAULT } = this.args;

    if (this.fastboot?.isFastBoot) return tag;

    return verifyHtmlTag(ELEMENT_TAG_DEFAULT, tag);
  }

  // Warns when an expanding flex is applied but the parent isn't a flex container, so the
  // flex has no effect. Runs once per instance from setup (debug/test only).
  #warnInertFlex() {
    if (this.mainAxis !== NONE || !this.isFlexExpanding) return;

    Debug.warn(
      `@flex was provided to ${this.componentName} but has no effect because the parent is not a flex container [instance: ${this.id}]`,
      false,
      { id: 'spds.container.flex-inert-parent' }
    );
  }

  ignoreClick = evt => {
    evt.stopPropagation();
  };

  /**
   * Override-relationship hook consumed by `warnOverriddenArguments`. Decides whether a
   * declared @flex override actually fires at runtime, accounting for the parent's main
   * axis and the effective (shorthand + longhand) flex — conditions a static `when` can't
   * express.
   * @param {string} argName
   * @param {{ name: string, axis: string, reason: string }} entry
   * @returns {boolean}
   */
  isArgumentOverridden = (argName, entry) => {
    if (argName !== 'flex') return false;
    if (!this.isFlexExpanding) return false;
    if (this.mainAxis === NONE) return false;

    const axisMatches = entry.axis === BOTH || entry.axis === this.mainAxis;

    if (!axisMatches) return false;

    return this[entry.name] ?? false;
  };

  setup = () => {
    // Measure the parent's main axis once (one getComputedStyle per instance); cached so the
    // flex getter never reflows on re-render.
    this.resolvedMainAxis = this.parentMainAxis;

    runInDebug(() => this.#warnInertFlex());

    if (!(this.shouldDiscardClick && this.baseElement)) return;

    this.baseElement.addEventListener('click', this.ignoreClick);

    registerDestructor(this, () => {
      this.baseElement.removeEventListener('click', this.ignoreClick);
    });
  };

  <template>
    {{#let (element this.tag) as |Tag|}}
      <Tag
        class={{this.classes}}
        data-test-spds-container={{this.tag}}
        id={{this.id}}
        style={{this.customProperties}}
        {{this.setupComponent}}
        {{this.validateArguments}}
        ...attributes
      >
        {{#if @content}}
          {{this.content}}
        {{else}}
          {{yield}}
        {{/if}}
      </Tag>
    {{/let}}
  </template>
}

export const CORE_CONTAINER_METADATA = SpdsCoreContainer.metadata;
